import React, { useState, useEffect } from "react";
import { auctionApi, eauctionHeaderApi, IMAGE_BASE_URL } from "../utils/api";
import { Loader2, Eye } from "lucide-react";
import "./Auction.css";
import SuccessModal from "../components/common/SuccessModal";
import PreviewModal from "../components/common/PreviewModal";

const Auction = () => {
  const [formData, setFormData] = useState({
    participantName: "",
    businessName: "",
    address: "",
    gstNumber: "",
    mobile: "",
    email: "",
    gstCert: null,
    agree: false,
    siteId: "ParekhRayon05"
  });

  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [showSuccess, setShowSuccess] = useState(false);
  const [showPreview, setShowPreview] = useState(false);

  const [auctions, setAuctions] = useState([]);
  const [header, setHeader] = useState({ title: "e-Auction Participation", description: "" });
  const [dataLoading, setDataLoading] = useState(true);

  useEffect(() => {
    const fetchAuctionData = async () => {
      try {
        setDataLoading(true);
        const [headerRes, auctionsRes] = await Promise.all([
          eauctionHeaderApi.get(formData.siteId),
          auctionApi.getAll(formData.siteId)
        ]);

        if (headerRes.data?.success && headerRes.data?.data) {
          setHeader({
            title: headerRes.data.data.title || "e-Auction Participation",
            description: headerRes.data.data.description || ""
          });
        }
        if (auctionsRes.data?.success && auctionsRes.data?.data) {
          const activeAuctions = auctionsRes.data.data.filter(a => a.status === 'active');
          setAuctions(activeAuctions);
        }
      } catch (error) {
        console.error("Error fetching auction data:", error);
      } finally {
        setDataLoading(false);
      }
    };
    fetchAuctionData();
  }, [formData.siteId]);


  const handleChange = (e) => {
    const { name, value, type, checked, files } = e.target;
    if (type === "checkbox") {
      setFormData((prev) => ({ ...prev, [name]: checked }));
    } else if (type === "file") {
      setFormData((prev) => ({ ...prev, [name]: files[0] }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handlePreview = (e) => {
    e.preventDefault();
    if (!formData.agree) {
      alert("Please confirm that all provided details are legally valid.");
      return;
    }
    setShowPreview(true);
  };

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();

    if (!formData.agree) {
      alert("Please confirm that all provided details are legally valid.");
      return;
    }

    setLoading(true);
    setShowPreview(false);
    setMessage({ type: '', text: '' });

    try {
      const data = new FormData();
      data.append("siteId", formData.siteId);
      data.append("participantName", formData.participantName);
      data.append("legalBusinessName", formData.businessName);
      data.append("businessAddress", formData.address);
      data.append("gstNo", formData.gstNumber);
      data.append("mobileNo", formData.mobile);
      data.append("email", formData.email);

      if (formData.gstCert) {
        data.append("gstCertificate", formData.gstCert);
      }

      const response = await auctionApi.submit(data);

      if (response.data.success) {
        setShowSuccess(true);
        setFormData({
          participantName: "",
          businessName: "",
          address: "",
          gstNumber: "",
          mobile: "",
          email: "",
          gstCert: null,
          agree: false,
          siteId: "ParekhRayon05"
        });
      } else {
        setMessage({ type: 'error', text: response.data.message || 'Something went wrong.' });
      }
    } catch (error) {
      console.error("Submission Error:", error);
      setMessage({ type: 'error', text: 'Server error. Please try again later.' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="auction-section">
      <div className="auction-container">
        <div className="auction-panel status-panel">
          <div className="status-card">
            <h4>{header.title}</h4>
            {header.description && (
              <div
                className="rte-content"
                style={{ fontSize: '15px', color: '#555', lineHeight: '1.6', marginTop: '10px' }}
                dangerouslySetInnerHTML={{ __html: header.description.replace(/&nbsp;/gi, " ").replace(/\u00a0/g, " ") }}
              />
            )}

            {dataLoading ? (
              <div style={{ display: 'flex', justifyContent: 'center', marginTop: '40px' }}>
                <Loader2 className="animate-spin" size={32} color="#717fe0" />
              </div>
            ) : auctions.length > 0 ? (
              <div className="active-auctions-list" style={{ marginTop: '30px' }}>
                <h4 style={{ marginBottom: '20px', color: '#5a5cff', fontSize: '20px', fontWeight: '800', textTransform: 'uppercase' }}>Active Auctions</h4>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                  {auctions.map(auction => (
                    <div key={auction._id} style={{ background: '#fff', padding: '20px', borderRadius: '12px', border: '1px solid #d8dcef', boxShadow: '0 4px 15px rgba(0,0,0,0.03)' }}>
                      {auction.image && (
                        <img
                          src={auction.image.startsWith('http') ? auction.image : `${IMAGE_BASE_URL}/${auction.image.replace(/\\/g, '/')}`}
                          alt={auction.title}
                          style={{ width: '100%', height: '200px', objectFit: 'cover', borderRadius: '8px', marginBottom: '15px' }}
                        />
                      )}
                      <h5 style={{ fontSize: '18px', fontWeight: '800', marginBottom: '10px', color: '#252e5a' }}>{auction.title}</h5>
                      <div
                        className="rte-content"
                        dangerouslySetInnerHTML={{ __html: auction.description ? auction.description.replace(/&nbsp;/gi, " ").replace(/\u00a0/g, " ") : "" }}
                        style={{ fontSize: '14px', color: '#525f7f', marginBottom: '15px', lineHeight: '1.6' }}
                      />
                      {auction.date && (
                        <p style={{ fontSize: '13px', color: '#717fe0', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '1px', margin: 0 }}>
                          Date: {new Date(auction.date).toLocaleDateString()}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div style={{ marginTop: '30px' }}>
                <div className="status-badge">
                  <span>Status:</span>
                  <h2>No e-Auction published</h2>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="auction-panel form-panel">
          <form className="auction-form" onSubmit={handleSubmit}>
            <h4>Participant Details</h4>

            {message.text && message.type === 'error' && (
              <div className={`message-alert ${message.type}`}>
                {message.text}
              </div>
            )}

            <div className="form-group">
              <input
                type="text"
                name="participantName"
                value={formData.participantName}
                onChange={handleChange}
                placeholder="Name of the Participant"
                required
              />
            </div>

            <div className="form-group">
              <input
                type="text"
                name="businessName"
                value={formData.businessName}
                onChange={handleChange}
                placeholder="Legal Name of the Business"
                required
              />
            </div>

            <div className="form-group">
              <textarea
                name="address"
                value={formData.address}
                onChange={handleChange}
                placeholder="Business Address with Pin code"
                required
              />
            </div>

            <div className="row two-column">
              <div className="col">
                <div className="form-group">
                  <input
                    type="text"
                    name="gstNumber"
                    value={formData.gstNumber}
                    onChange={handleChange}
                    placeholder="GST No."
                    required
                  />
                </div>
              </div>
            </div>

            <div className="row two-column">
              <div className="col">
                <div className="form-group">
                  <input
                    type="text"
                    name="mobile"
                    value={formData.mobile}
                    onChange={handleChange}
                    placeholder="Mobile No."
                    required
                  />
                </div>
              </div>
              <div className="col">
                <div className="form-group">
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="Email Id"
                    required
                  />
                </div>
              </div>
            </div>

            <div className="form-group file-group">
              <label>Upload GST Certificate (PDF/JPG)</label>
              <input
                type="file"
                name="gstCert"
                onChange={handleChange}
                accept=".pdf,.jpg,.jpeg,.png"
              />
            </div>

            <div className="form-group checkbox-group">
              <label>
                <input
                  type="checkbox"
                  name="agree"
                  checked={formData.agree}
                  onChange={handleChange}
                />
                I confirm all provided details are legally valid.
              </label>
            </div>

            <div className="form-actions-flex" style={{ display: 'flex', gap: '15px' }}>
              <button type="button" className="preview-button-auction" onClick={handlePreview}>
                <Eye size={18} className="m-r-8" /> Preview
              </button>
              <button type="submit" className="submit-button" disabled={loading}>
                {loading ? <Loader2 className="animate-spin" /> : "REGISTER FOR AUCTION"}
              </button>
            </div>

            <div className="form-footer-link" style={{ textAlign: 'center', marginTop: '10px' }}>
              <a href="mailto:services@parekhrayon.com">services@parekhrayon.com</a>
            </div>

            {/* <div className="terms-link">
              <a href="#">View Participant Terms & Conditions</a>
            </div> */}
          </form>
        </div>
      </div>

      <PreviewModal
        isOpen={showPreview}
        onClose={() => setShowPreview(false)}
        data={formData}
        title="Auction Registration Preview"
        onConfirm={handleSubmit}
        loading={loading}
      />

      <SuccessModal
        isOpen={showSuccess}
        onClose={() => setShowSuccess(false)}
        title="Registration Successful"
        message="Thank you for registering for the e-Auction. Your details have been submitted and our team will contact you once the auction is published."
      />
    </section>
  );
};

export default Auction;
